"use client"

import { useState, useEffect, useRef } from "react"
import { Link } from "react-router-dom"
import {
  Box, Typography,
  Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Button, TextField, CircularProgress, InputAdornment,
} from "@mui/material"
import Close from "@mui/icons-material/Close"
import EditIcon from "@mui/icons-material/Edit"
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera"
import {
  FaUsers, FaClock, FaMapMarkerAlt, FaMusic, FaUtensils, FaParking, FaWifi, FaSwimmingPool, FaUmbrellaBeach,
} from "react-icons/fa"
import { collection, getDocs, doc, setDoc, updateDoc, addDoc, deleteDoc } from "firebase/firestore"
import { ref, uploadBytes, getDownloadURL } from "firebase/storage"
import { db, storage } from "../config/firebase"
import { useAuth } from "../AuthContext"

import bougie from "../assets/images/PBPavillion-Images/bougie.png"
import uniliveonce from "../assets/images/PBPavillion-Images/uniliveonce.png"
import sushihouse from "../assets/images/PBPavillion-Images/sushihouse.png"
import inhomesushi from "../assets/images/PBPavillion-Images/inhomesushi.png"
import tunaspectacle from "../assets/images/PBPavillion-Images/tunaspectacle.png"
import sushiclass from "../assets/images/PBPavillion-Images/sushiclass.png"
import Footer from "./Footer"
import PageHeader from "./PageHeader"

const IMAGE_MAP = { bougie, uniliveonce, sushihouse, inhomesushi, tunaspectacle, sushiclass }

const DEFAULT_EVENTS = [
  {
    id: "pavilion-1", title: "Legacy Lounge: Baldwin & Bubbles", price: "100", imageKey: "inhomesushi",
    details: [
      "A LITERARY TOAST TO CULTURE AND TASTE",
      "2-hour sunset lounge experience",
      "Charcuterie and signature Afro-Latin tapas",
      "Champagne, Prosecco, & artisanal mocktails",
      "Minimum of 6 guests",
    ],
  },
  {
    id: "pavilion-2", title: "Civic Spirit: Hamilton Pour", price: "110", imageKey: "sushihouse",
    details: [
      "REVOLUTIONARY FLAVORS, SERVED IN STYLE",
      "2-hour cocktail-forward lounge tasting",
      "Elevated hors d'oeuvres with bold global notes",
      "Curated wine + cocktail flight (3 rounds)",
      "Minimum of 6 guests",
    ],
  },
  {
    id: "pavilion-3", title: "Island Revival: Garifuna Gold", price: "125", imageKey: "uniliveonce",
    details: [
      "ROOTED IN HERITAGE, INFUSED WITH SPICE",
      "90-minute communal table experience",
      "10-course Afro-Caribbean tasting menu",
      "90-minute rum and fresh-juice cocktail bar",
      "Minimum of 6 guests",
    ],
  },
  {
    id: "pavilion-4", title: "Prime Roots: Agape Garden Table", price: "135", imageKey: "tunaspectacle",
    details: [
      "FRESH, BOTANICAL, AND FARM-TO-GLASS",
      "90-minute guided food & drink pairing session",
      "Garden-sourced tasting menu (vegetarian optional)",
      "Floral & fruit-forward cocktail and wine pairings",
      "Minimum of 4 guests",
    ],
  },
  {
    id: "pavilion-5", title: "The American Dream: Tubman", price: "150", imageKey: "bougie",
    details: [
      "PASSED COMMUNITY BITES FOR YOU AND YOUR GUESTS",
      "2-hour seated event",
      "12-course private provisions",
      "2-hour signature libations of your choice",
      "Minimum of 8 guests",
    ],
  },
  {
    id: "pavilion-6", title: "Liberation Fire: Maroon Feast", price: "160", imageKey: "sushiclass",
    details: [
      "CELEBRATING RESILIENCE THROUGH FLAVOR",
      "2-hour long-table feast",
      "5 fire-grilled, chef-prepared courses",
      "Bold aged spirits, mezcal, and smoked cocktails",
      "Live music or spoken word (optional add-on)",
      "Minimum of 10 guests",
    ],
  },
]

const INTER_FONT = {
  fontFamily: "'Inter', 'Satoshi', Arial, Helvetica, sans-serif",
  fontWeight: 575,
  letterSpacing: "0.25px",
}

const modernDisplayFont = "Satoshi, Inter, SF Pro Display, system-ui, -apple-system, sans-serif"
const modernBodyFont = "Inter, SF Pro Text, system-ui, -apple-system, sans-serif"

const getAmenityIcon = (text) => {
  const lowerText = text.toLowerCase()
  if (lowerText.includes("capacity") || lowerText.includes("guests") || lowerText.includes("people")) return <FaUsers />
  if (lowerText.includes("time") || lowerText.includes("hours") || lowerText.includes("duration")) return <FaClock />
  if (lowerText.includes("location") || lowerText.includes("venue") || lowerText.includes("address"))
    return <FaMapMarkerAlt />
  if (lowerText.includes("music") || lowerText.includes("sound") || lowerText.includes("audio")) return <FaMusic />
  if (lowerText.includes("catering") || lowerText.includes("food") || lowerText.includes("dining"))
    return <FaUtensils />
  if (lowerText.includes("parking") || lowerText.includes("valet")) return <FaParking />
  if (lowerText.includes("wifi") || lowerText.includes("internet")) return <FaWifi />
  if (lowerText.includes("pool") || lowerText.includes("swimming")) return <FaSwimmingPool />
  if (lowerText.includes("beach") || lowerText.includes("ocean")) return <FaUmbrellaBeach />
  if (
    lowerText.includes("drink") ||
    lowerText.includes("beverage") ||
    lowerText.includes("cocktail") ||
    lowerText.includes("bar")
  )
    return <FaUtensils />
  if (lowerText.includes("setup") || lowerText.includes("decoration") || lowerText.includes("decor")) return <FaMusic />
  if (lowerText.includes("service") || lowerText.includes("staff") || lowerText.includes("attendant"))
    return <FaUsers />
  return <FaMapMarkerAlt /> // Default icon
}

const resolveImage = (event) => {
  if (event.imageUrl) return event.imageUrl
  if (event.imageKey && IMAGE_MAP[event.imageKey]) return IMAGE_MAP[event.imageKey]
  return inhomesushi
}

// Strip everything but digits/decimal point, then re-add a single leading $ — handles legacy data too
const sanitizePrice = (raw) => {
  let value = String(raw || "").replace(/[^0-9.]/g, "")
  const firstDot = value.indexOf(".")
  if (firstDot !== -1) {
    value = value.slice(0, firstDot + 1) + value.slice(firstDot + 1).replace(/\./g, "")
  }
  const [whole, decimal] = value.split(".")
  return decimal !== undefined ? `${whole}.${decimal.slice(0, 2)}` : value
}

const formatPrice = (raw) => {
  const cleaned = sanitizePrice(raw)
  return cleaned ? `$${cleaned}` : "—"
}

const textShadowStyle = { textShadow: "0 2px 8px rgba(0,0,0,0.3)" }

export default function PavillionCard() {
  const { isAdmin } = useAuth()
  const [events, setEvents]       = useState([])
  const [loading, setLoading]     = useState(true)
  const [popupImage, setPopupImage] = useState(null)

  // Edit state
  const [editEvent, setEditEvent]       = useState(null)
  const [editForm, setEditForm]         = useState({})
  const [saving, setSaving]             = useState(false)
  const [savedMsg, setSavedMsg]         = useState(false)
  const [uploadingImg, setUploadingImg] = useState(false)
  const [previewUrl, setPreviewUrl]     = useState(null)
  const fileInputRef = useRef(null)

  // ── Load / seed ──────────────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      try {
        const snap = await getDocs(collection(db, "pavilionEvents"))
        if (snap.empty) {
          const seeded = DEFAULT_EVENTS.map((ev) => ({ ...ev, available: true }))
          await Promise.all(seeded.map((ev) => setDoc(doc(db, "pavilionEvents", ev.id), ev)))
          setEvents(seeded)
        } else {
          const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }))
          data.sort((a, b) => a.id.localeCompare(b.id))
          setEvents(data)
        }
      } catch (err) {
        console.error(err)
        setEvents(DEFAULT_EVENTS)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [])

  // ── Edit open / close ────────────────────────────────────────────────
  const handleEditOpen = (e, event) => {
    if (e) e.stopPropagation()
    setEditEvent(event)
    setPreviewUrl(resolveImage(event))
    setEditForm({
      title:       event.title || "",
      price:       sanitizePrice(event.price),
      detailsText: (event.details || []).join("\n"),
    })
    setSavedMsg(false)
  }

  const handleEditClose = () => {
    setEditEvent(null)
    setEditForm({})
    setPreviewUrl(null)
    setSavedMsg(false)
  }

  // ── Image upload ─────────────────────────────────────────────────────
  const handleImageChange = async (e) => {
    const file = e.target.files[0]
    if (!file) return

    setPreviewUrl(URL.createObjectURL(file))
    setUploadingImg(true)

    try {
      const storageRef = ref(storage, `pavilion-images/${editEvent.id}-${Date.now()}`)
      await uploadBytes(storageRef, file)
      const downloadUrl = await getDownloadURL(storageRef)

      await updateDoc(doc(db, "pavilionEvents", editEvent.id), { imageUrl: downloadUrl })

      setEvents((prev) =>
        prev.map((ev) => ev.id === editEvent.id ? { ...ev, imageUrl: downloadUrl } : ev)
      )
      setEditEvent((prev) => ({ ...prev, imageUrl: downloadUrl }))
      setPreviewUrl(downloadUrl)
    } catch (err) {
      console.error("Image upload error:", err)
    } finally {
      setUploadingImg(false)
    }
  }

  // ── Add new / Delete ─────────────────────────────────────────────────
  const handleAddNew = async () => {
    const newEvent = {
      title: "New PB Pavilion Experience",
      price: "0",
      details: ["Add details here"],
      available: true,
    }
    try {
      const ref = await addDoc(collection(db, "pavilionEvents"), newEvent)
      const created = { id: ref.id, ...newEvent }
      setEvents((prev) => [...prev, created])
      handleEditOpen(null, created)
    } catch (err) {
      console.error("Add event error:", err)
    }
  }

  const handleDelete = async () => {
    if (!window.confirm("Delete this experience? This cannot be undone.")) return
    setSaving(true)
    try {
      await deleteDoc(doc(db, "pavilionEvents", editEvent.id))
      setEvents((prev) => prev.filter((ev) => ev.id !== editEvent.id))
      handleEditClose()
    } catch (err) {
      console.error("Delete event error:", err)
    } finally {
      setSaving(false)
    }
  }

  // ── Availability toggle ───────────────────────────────────────────────
  const handleToggleAvailability = async (e, event) => {
    e.stopPropagation()
    const newAvailable = event.available === false
    try {
      await updateDoc(doc(db, "pavilionEvents", event.id), { available: newAvailable })
      setEvents((prev) =>
        prev.map((ev) => ev.id === event.id ? { ...ev, available: newAvailable } : ev)
      )
      if (editEvent?.id === event.id) {
        setEditEvent((prev) => ({ ...prev, available: newAvailable }))
      }
    } catch (err) {
      console.error("Availability toggle error:", err)
    }
  }

  // ── Save text fields ─────────────────────────────────────────────────
  const handleEditSave = async () => {
    setSaving(true)
    try {
      const updatedDetails = editForm.detailsText
        .split("\n").map((t) => t.trim()).filter(Boolean)

      const updated = {
        title:   editForm.title,
        price:   editForm.price,
        details: updatedDetails,
      }

      await updateDoc(doc(db, "pavilionEvents", editEvent.id), updated)

      setEvents((prev) =>
        prev.map((ev) => ev.id === editEvent.id ? { ...ev, ...updated } : ev)
      )
      setSavedMsg(true)
      setTimeout(handleEditClose, 1200)
    } catch (err) {
      console.error(err)
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
        <Typography sx={{ color: "white", fontFamily: modernBodyFont }}>Loading...</Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ position: "relative", minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <video autoPlay loop muted playsInline
        style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", objectFit: "cover", zIndex: -1 }}>
        <source src="/videos/PavilionVid.mp4" type="video/mp4" />
      </video>

      <Box sx={{ width: "100%", minHeight: "100vh", display: "flex", flexDirection: "column", position: "relative", zIndex: 1, flex: 1 }}>
        <Box sx={{ px: { xs: 2, md: 4 }, py: 4, paddingTop: 3, flex: 1 }}>

          {/* Header */}
          <PageHeader
            docId="pavilion-header"
            defaultTitle="PB Pavilion"
            defaultDescription="Experience the heart of community, creativity and culinary craftsmanship. The PB Pavilion hosts unforgettable events, delicious culinary experiences, and moments you'll remember for a lifetime. Reserve your spot now!"
            descSx={{ letterSpacing: "-0.01em", color: "rgb(253, 250, 250)" }}
          />

          {/* Cards */}
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2,1fr)", lg: "repeat(3,1fr)" }, gap: 4, mt: 4, justifyItems: "center", alignItems: "stretch", width: "100%", maxWidth: "1400px", margin: "0 auto" }}>
            {events.map((event) => (
              <div
                key={event.id}
                style={{
                  background: "rgba(255, 255, 255, 0.08)",
                  backdropFilter: "blur(12px)",
                  WebkitBackdropFilter: "blur(12px)",
                  border: event.available !== false ? "1px solid rgba(255, 255, 255, 0.15)" : "1px solid rgba(185,28,28,0.4)",
                  borderRadius: "1.2rem",
                  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
                  padding: "20px",
                  width: "100%",
                  maxWidth: 440,
                  minWidth: 0,
                  height: "auto",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "flex-start",
                  alignItems: "center",
                  textAlign: "center",
                  fontFamily: INTER_FONT.fontFamily,
                  color: "#ffffff",
                  margin: "0 auto",
                  position: "relative",
                  opacity: event.available !== false ? 1 : 0.7,
                }}
              >
                {/* Pencil icon — admin only */}
                {isAdmin && (
                  <IconButton onClick={(e) => handleEditOpen(e, event)} size="small"
                    sx={{ position: "absolute", top: 8, left: 8, zIndex: 10, backgroundColor: "rgba(255,255,255,0.92)", width: 30, height: 30, boxShadow: "0 2px 8px rgba(0,0,0,0.3)", "&:hover": { backgroundColor: "#fff", transform: "scale(1.1)" } }}>
                    <EditIcon sx={{ fontSize: 15, color: "#0d1b4c" }} />
                  </IconButton>
                )}

                {/* Availability badge — visible to all; admins can click to toggle */}
                <Box
                  onClick={isAdmin ? (e) => handleToggleAvailability(e, event) : undefined}
                  sx={{ position: "absolute", top: 8, left: isAdmin ? 46 : 8, zIndex: 10, cursor: isAdmin ? "pointer" : "default", backgroundColor: event.available !== false ? "rgba(22,163,74,0.9)" : "rgba(185,28,28,0.85)", color: "#fff", px: 1, py: 0.25, borderRadius: "12px", fontSize: "0.65rem", fontWeight: 700, lineHeight: "20px", height: 20, display: "flex", alignItems: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.3)", userSelect: "none", ...(isAdmin && { "&:hover": { filter: "brightness(1.1)" } }) }}>
                  {event.available !== false ? "Available" : "Unavailable"}
                </Box>

                {/* Image section */}
                <div
                  style={{
                    width: "100%",
                    height: "270px",
                    overflow: "hidden",
                    borderRadius: "0.8rem",
                    marginBottom: 24,
                    transition: "transform 0.4s ease-in-out",
                    cursor: "pointer",
                    boxShadow: "0 4px 16px rgba(0, 0, 0, 0.3)",
                  }}
                  onMouseOver={(e) => (e.currentTarget.style.transform = "scale(1.05)")}
                  onMouseOut={(e) => (e.currentTarget.style.transform = "scale(1)")}
                  onClick={() => setPopupImage(resolveImage(event))}
                >
                  <img src={resolveImage(event)} alt={event.title}
                    style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                </div>

                <h2 style={{ fontSize: "2rem", marginBottom: 8, fontFamily: "'Inter', 'SF Pro Text', system-ui, sans-serif", fontWeight: 600, color: "#ffffff", letterSpacing: "-0.01em", ...textShadowStyle }}>
                  {event.title}
                </h2>

                <div style={{ position: "relative", backgroundColor: "rgba(15, 23, 42, 0.9)", backdropFilter: "blur(8px)", border: "1px solid rgba(255, 255, 255, 0.1)", borderRadius: "8px", padding: "6px 12px", marginBottom: 16, boxShadow: "0 4px 16px rgba(0, 0, 0, 0.3)" }}>
                  <p style={{ fontWeight: 700, margin: 0, color: "#ffffff", fontSize: "1rem", fontFamily: "'Inter', 'SF Pro Text', system-ui, sans-serif", letterSpacing: "0.02em", textShadow: "0 2px 4px rgba(0, 0, 0, 0.5)" }}>
                    {formatPrice(event.price)}
                    <span style={{ fontWeight: 300, color: "rgba(203, 213, 225, 1)", fontSize: "0.8rem", marginLeft: "4px" }}>
                      + PER PERSON
                    </span>
                  </p>
                </div>

                <div style={{ maxHeight: "150px", overflowY: "auto", width: "100%", paddingRight: "8px", marginBottom: "16px" }}>
                  <ul style={{ listStyle: "none", padding: 0, fontSize: "1rem", lineHeight: "1.6", textAlign: "left", maxWidth: "320px", margin: "0 auto", fontFamily: "'Inter', 'SF Pro Text', system-ui, sans-serif", fontWeight: 400, color: "rgba(255, 255, 255, 0.9)", ...textShadowStyle }}>
                    {(event.details || []).map((item, i) => (
                      <li key={i} style={{ marginBottom: "8px", display: "flex", alignItems: "center" }}>
                        <span style={{ color: "rgb(147, 197, 253)", marginRight: 8, fontSize: "0.9rem", ...textShadowStyle }}>
                          {getAmenityIcon(item)}
                        </span>
                        <span style={{ color: "rgba(255, 255, 255, 0.9)", ...textShadowStyle }}>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div style={{ flexGrow: 1 }} />

                <Button component={Link} to="/reserve-pavilion" variant="contained"
                  sx={{ padding: "12px 28px", backgroundColor: "rgba(15, 23, 42, 0.9)", backdropFilter: "blur(4px)", border: "1px solid rgba(255, 255, 255, 0.1)", borderRadius: "12px", color: "#ffffff", fontWeight: 600, fontFamily: "'Inter', 'SF Pro Text', system-ui, sans-serif", marginTop: "20px", letterSpacing: "0.02em", textShadow: "0 2px 8px rgba(0,0,0,0.3)", boxShadow: "0 4px 16px rgba(0, 0, 0, 0.3)", "&:hover": { backgroundColor: "rgba(15, 23, 42, 1)", transform: "translateY(-2px)", boxShadow: "0 6px 20px rgba(0, 0, 0, 0.4)" } }}>
                  BOOK NOW
                </Button>
              </div>
            ))}
          </Box>

          {/* Add New button — admin only */}
          {isAdmin && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
              <Button
                variant="outlined"
                onClick={handleAddNew}
                sx={{ color: "white", borderColor: "rgba(255,255,255,0.4)", borderRadius: "10px", px: 3, py: 1.25, fontSize: "0.9rem", fontWeight: 600, letterSpacing: "0.02em", backdropFilter: "blur(8px)", backgroundColor: "rgba(255,255,255,0.07)", "&:hover": { borderColor: "white", backgroundColor: "rgba(255,255,255,0.14)" } }}
              >
                + Add PB Pavilion Experience
              </Button>
            </Box>
          )}
        </Box>

        {/* Fullscreen Image Popup */}
        {popupImage && (
          <div
            onClick={() => setPopupImage(null)}
            style={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", backgroundColor: "rgba(0, 0, 0, 0.8)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 9999, cursor: "pointer", backdropFilter: "blur(4px)" }}
          >
            <img src={popupImage} alt="Full screen"
              style={{ maxWidth: "90vw", maxHeight: "90vh", borderRadius: "12px", boxShadow: "0 0 40px rgba(0, 0, 0, 0.8)" }} />
          </div>
        )}

        {/* Admin Edit Dialog */}
        <Dialog open={Boolean(editEvent)} onClose={handleEditClose} maxWidth="sm" fullWidth
          PaperProps={{ sx: { backgroundColor: "#0d1b4c", border: "1px solid rgba(255,255,255,0.15)", borderRadius: "16px" } }}>
          {editEvent && (
            <>
              <DialogTitle sx={{ fontFamily: modernDisplayFont, fontWeight: 600, color: "white", display: "flex", alignItems: "center", gap: 1 }}>
                <EditIcon sx={{ fontSize: 18 }} /> Edit — {editEvent.title}
                <IconButton onClick={handleEditClose} sx={{ position: "absolute", right: 8, top: 8, color: "white" }}><Close /></IconButton>
              </DialogTitle>

              <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>

                {/* Image upload section */}
                <Box>
                  <Typography sx={{ color: "rgba(255,255,255,0.6)", fontSize: 12, letterSpacing: 1, textTransform: "uppercase", mb: 1 }}>
                    Event Photo
                  </Typography>
                  <Box sx={{ position: "relative", borderRadius: "10px", overflow: "hidden", mb: 1 }}>
                    <img src={previewUrl} alt="preview" style={{ width: "100%", height: 160, objectFit: "cover", display: "block", borderRadius: 10 }} />
                    {uploadingImg && (
                      <Box sx={{ position: "absolute", inset: 0, backgroundColor: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: "10px" }}>
                        <CircularProgress size={32} sx={{ color: "white" }} />
                      </Box>
                    )}
                  </Box>
                  <input type="file" accept="image/*" ref={fileInputRef} onChange={handleImageChange} style={{ display: "none" }} />
                  <Button variant="outlined" size="small" startIcon={<PhotoCameraIcon />}
                    onClick={() => fileInputRef.current.click()} disabled={uploadingImg}
                    sx={{ color: "rgba(255,255,255,0.7)", borderColor: "rgba(255,255,255,0.25)", borderRadius: "8px", fontSize: 12, "&:hover": { borderColor: "rgba(255,255,255,0.6)", backgroundColor: "rgba(255,255,255,0.05)" } }}>
                    {uploadingImg ? "Uploading..." : "Change Photo"}
                  </Button>
                </Box>

                {/* Title */}
                <TextField label="Event Name" value={editForm.title || ""} onChange={(e) => setEditForm((p) => ({ ...p, title: e.target.value }))} fullWidth size="small"
                  helperText='Full display name shown on the card — e.g. "Legacy Lounge: Baldwin & Bubbles"'
                  InputLabelProps={{ style: { color: "rgba(255,255,255,0.6)" } }}
                  InputProps={{ style: { color: "white" } }}
                  FormHelperTextProps={{ style: { color: "rgba(255,255,255,0.45)", fontSize: "0.72rem" } }}
                  sx={{ "& .MuiOutlinedInput-root": { "& fieldset": { borderColor: "rgba(255,255,255,0.2)" }, "&:hover fieldset": { borderColor: "rgba(255,255,255,0.5)" }, "&.Mui-focused fieldset": { borderColor: "rgba(255,255,255,0.8)" } } }}
                />

                {/* Price — $ prefix locked in, digits only */}
                <TextField label="Starting Price" value={editForm.price || ""}
                  onChange={(e) => setEditForm((p) => ({ ...p, price: sanitizePrice(e.target.value) }))}
                  fullWidth size="small"
                  helperText="Numbers only (decimals OK, e.g. 149.99) — the $ is added automatically. Shown as $XXX+ PER PERSON on the card."
                  InputLabelProps={{ style: { color: "rgba(255,255,255,0.6)" } }}
                  InputProps={{
                    style: { color: "white" },
                    startAdornment: <InputAdornment position="start" sx={{ "& .MuiTypography-root": { color: "rgba(255,255,255,0.8)", fontWeight: 600 } }}>$</InputAdornment>,
                    inputMode: "numeric",
                  }}
                  FormHelperTextProps={{ style: { color: "rgba(255,255,255,0.45)", fontSize: "0.72rem" } }}
                  sx={{ "& .MuiOutlinedInput-root": { "& fieldset": { borderColor: "rgba(255,255,255,0.2)" }, "&:hover fieldset": { borderColor: "rgba(255,255,255,0.5)" }, "&.Mui-focused fieldset": { borderColor: "rgba(255,255,255,0.8)" } } }}
                />

                {/* Package details */}
                <TextField label="Package Details (one per line)" value={editForm.detailsText || ""} onChange={(e) => setEditForm((p) => ({ ...p, detailsText: e.target.value }))} fullWidth multiline rows={6}
                  helperText='Type one detail per line, shown with icons on the card — e.g. "2-hour seated event"'
                  InputLabelProps={{ style: { color: "rgba(255,255,255,0.6)" } }}
                  InputProps={{ style: { color: "white", fontSize: "0.9rem" } }}
                  FormHelperTextProps={{ style: { color: "rgba(255,255,255,0.45)", fontSize: "0.72rem" } }}
                  sx={{ "& .MuiOutlinedInput-root": { "& fieldset": { borderColor: "rgba(255,255,255,0.2)" }, "&:hover fieldset": { borderColor: "rgba(255,255,255,0.5)" }, "&.Mui-focused fieldset": { borderColor: "rgba(255,255,255,0.8)" } } }}
                />

                {/* Availability toggle */}
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: 1.5, borderRadius: "8px", border: "1px solid rgba(255,255,255,0.15)", backgroundColor: "rgba(255,255,255,0.04)" }}>
                  <Box>
                    <Typography sx={{ color: "white", fontSize: 14, fontWeight: 500 }}>Availability</Typography>
                    <Typography sx={{ color: "rgba(255,255,255,0.45)", fontSize: "0.72rem", mt: 0.25 }}>
                      {editEvent.available !== false ? "Accepting bookings — visible in booking form" : "Bookings paused — hidden from booking form"}
                    </Typography>
                  </Box>
                  <Box onClick={(e) => handleToggleAvailability(e, editEvent)}
                    sx={{ cursor: "pointer", backgroundColor: editEvent.available !== false ? "rgba(22,163,74,0.9)" : "rgba(185,28,28,0.85)", color: "#fff", px: 2, py: 0.75, borderRadius: "8px", fontSize: "0.8rem", fontWeight: 700, userSelect: "none", minWidth: 104, textAlign: "center", flexShrink: 0, transition: "background 0.2s", "&:hover": { filter: "brightness(1.1)" } }}>
                    {editEvent.available !== false ? "Available" : "Unavailable"}
                  </Box>
                </Box>
              </DialogContent>

              <DialogActions sx={{ px: 3, pb: 2, justifyContent: "space-between" }}>
                <Button onClick={handleDelete} disabled={saving}
                  sx={{ color: "#f87171", border: "1px solid rgba(248,113,113,0.4)", borderRadius: "8px", "&:hover": { backgroundColor: "rgba(248,113,113,0.1)", borderColor: "#f87171" } }}>
                  Delete Experience
                </Button>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button onClick={handleEditClose} sx={{ color: "rgba(255,255,255,0.6)", border: "1px solid rgba(255,255,255,0.2)", borderRadius: "8px" }}>Cancel</Button>
                  <Button variant="contained" onClick={handleEditSave} disabled={saving || uploadingImg}
                    sx={{ backgroundColor: savedMsg ? "#1a7a4a" : "white", color: savedMsg ? "white" : "#0d1b4c", fontWeight: 600, borderRadius: "8px", "&:hover": { backgroundColor: savedMsg ? "#1a7a4a" : "#f0f0f0" } }}>
                    {saving ? "Saving..." : savedMsg ? "✓ Saved!" : "Save Changes"}
                  </Button>
                </Box>
              </DialogActions>
            </>
          )}
        </Dialog>

        {/* Footer */}
        <Footer sx={{ marginTop: "auto", width: "100vw", left: "50%", marginLeft: "-50vw", marginRight: "-50vw", marginBottom: 0 }} />
      </Box>
    </Box>
  )
}
